import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'semantic-ui-react';
import debounce from 'lodash/debounce';
import { IF_NULL_OPTIONS, FIELDS_CONFIG } from './fieldsConfig';
import ValueSelector from './components/ValueSelector/Index';
import NoteInput from './components/NoteInput';
import StatusHistoryModal from './components/StatusHistoryModal';
import {
  useEligibilityActions,
  useEligibilityProducts,
} from '../../hooks/useEligibilityEngine';
import client from '../../apollo';
import upsertRuleNoteMutation from '../../graphql/upsertRuleNoteMutation';

const FieldRow = ({ rule }) => {
  const { onIfNullChange } = useEligibilityActions();
  const { selectedProduct } = useEligibilityProducts();

  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteText, setNoteText] = useState(rule.note || '');
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const debouncedUpsert = useCallback(
    debounce(note => {
      client.mutate({
        mutation: upsertRuleNoteMutation,
        variables: {
          input: {
            ruleNote: {
              productCode: selectedProduct,
              ruleId: rule.id,
              note,
            },
          },
        },
      });
    }, 1000),
    []
  );

  useEffect(() => () => debouncedUpsert.cancel(), [debouncedUpsert]);

  const handleNoteChange = useCallback(
    text => {
      setNoteText(text);
      debouncedUpsert(text);
    },
    [debouncedUpsert]
  );

  const handleMenuAction = action => {
    if (action === 'add_note') {
      setShowNoteInput(true);
    } else if (action === 'view_history') {
      setShowHistoryModal(true);
    }
  };

  const handleCloseNote = () => {
    debouncedUpsert.flush();
    setShowNoteInput(false);
  };

  const handleCloseHistory = () => {
    setShowHistoryModal(false);
  };

  return (
    <>
      <div className='field-row' data-testid={`field-row-${rule.id}`}>
        <div className='field-label' data-testid={`field-label-${rule.id}`}>
          {rule.name}
        </div>
        <div className='field-value'>
          <ValueSelector rule={rule} />
        </div>
        <div className='field-actions-column'>
          <Dropdown
            selection
            options={IF_NULL_OPTIONS}
            value={rule.actionOnNull}
            onChange={(e, data) => onIfNullChange(rule.id, data.value)}
            data-testid={`if-null-dropdown-${rule.id}`}
          />
        </div>

        <div className='field-menu-cell'>
          <div className='note-indicator-slot'>
            {noteText && (
              <i
                aria-hidden='true'
                className='icon note-indicator'
                data-testid={`note-indicator-${rule.id}`}
                onClick={() => handleMenuAction('add_note')}
              />
            )}
          </div>
          <Dropdown
            icon='ellipsis vertical'
            pointing='top right'
            className='actions-menu-dropdown'
            data-testid={`actions-menu-${rule.id}`}
          >
            <Dropdown.Menu>
              <Dropdown.Item
                text='Add a Note'
                onClick={() => handleMenuAction('add_note')}
              />
              <Dropdown.Item
                text='View History'
                onClick={() => handleMenuAction('view_history')}
              />
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {showNoteInput && (
          <div className='field-note-row' data-testid={`note-input-${rule.id}`}>
            <NoteInput
              noteText={noteText}
              onNoteChange={handleNoteChange}
              onClose={handleCloseNote}
              ruleId={rule.id}
            />
          </div>
        )}
      </div>

      <StatusHistoryModal
        open={showHistoryModal}
        onClose={handleCloseHistory}
        fieldLabel={rule.label || FIELDS_CONFIG.find(f => f.id === rule.name)?.label || rule.name}
        productCode={selectedProduct}
      />
    </>
  );
};

FieldRow.propTypes = {
  rule: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    valueType: PropTypes.oneOf([
      'link',
      'dropdown',
      'multiselect',
      'buckets',
      'include_multiselect',
      'number_input',
      'static_text',
      'comparison_value',
      'range',
      'include_exclude_multiselect',
      'condition_builder',
      'search_input',
      'time_picker',
      'empty',
    ]).isRequired,
    actionOnNull: PropTypes.string.isRequired,
    note: PropTypes.string,
  }).isRequired,
};

export default FieldRow;
